import React, { useState, useEffect } from "react";
import {
  Building2,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  User,
  Globe,
  FileText,
  X,
  Users,
} from "lucide-react";
import { useToast } from "../components/shared/ToastProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  "Unqualified",
  "Qualified Lead",
  "Proposal",
  "Negotiation",
  "Active Account",
  "Passive Account",
  "DNC",
  "Archive",
];

// Move VendorModal outside to prevent re-creation on every render
const VendorModal = ({
  isOpen,
  onClose,
  onSave,
  title,
  formData,
  setFormData,
  allContacts,
  selectedContacts,
  setSelectedContacts,
}) => {
  if (!isOpen) return null;

  const handleContactToggle = (contactId) => {
    if (selectedContacts.includes(contactId)) {
      setSelectedContacts(selectedContacts.filter((id) => id !== contactId));
    } else {
      setSelectedContacts([...selectedContacts, contactId]);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl my-8 mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-2xl font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="col-span-2">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                Basic Information
              </h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vendor Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.company_name}
                onChange={(e) =>
                  setFormData({ ...formData, company_name: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter vendor name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parent Company
              </label>
              <input
                type="text"
                value={formData.parent_company}
                onChange={(e) =>
                  setFormData({ ...formData, parent_company: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter parent company"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({ ...formData, status: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Account Manager <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.account_manager}
                onChange={(e) =>
                  setFormData({ ...formData, account_manager: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter account manager"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Website
              </label>
              <input
                type="url"
                value={formData.company_website}
                onChange={(e) =>
                  setFormData({ ...formData, company_website: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ownership Type
              </label>
              <input
                type="text"
                value={formData.ownership_type}
                onChange={(e) =>
                  setFormData({ ...formData, ownership_type: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Private, Public, LLC, etc."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Industry
              </label>
              <input
                type="text"
                value={formData.industry}
                onChange={(e) =>
                  setFormData({ ...formData, industry: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter industry"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Standard Perm Fee (%)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.standard_perm_fee_percentage}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    standard_perm_fee_percentage: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="20.0"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Overview
              </label>
              <textarea
                value={formData.company_overview}
                onChange={(e) =>
                  setFormData({ ...formData, company_overview: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="2"
                placeholder="Brief overview of the company"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Description
              </label>
              <textarea
                value={formData.company_description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    company_description: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows="3"
                placeholder="Detailed description"
              />
            </div>

            {/* Location Information */}
            <div className="col-span-2 mt-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                Location Information
              </h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Main Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.main_phone}
                onChange={(e) =>
                  setFormData({ ...formData, main_phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+1-555-0123"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Full address"
              />
            </div>

            {/* Billing Information */}
            <div className="col-span-2 mt-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                Billing Information
              </h4>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Billing Contact <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.billing_contact}
                onChange={(e) =>
                  setFormData({ ...formData, billing_contact: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Contact name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Billing Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={formData.billing_phone}
                onChange={(e) =>
                  setFormData({ ...formData, billing_phone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="+1-555-0124"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Billing Frequency
              </label>
              <select
                value={formData.billing_frequency}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    billing_frequency: e.target.value,
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Monthly">Monthly</option>
                <option value="Quarterly">Quarterly</option>
                <option value="Annually">Annually</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Billing Email
              </label>
              <input
                type="email"
                value={formData.billing_email}
                onChange={(e) =>
                  setFormData({ ...formData, billing_email: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="billing@example.com"
              />
            </div>

            {/* Contacts Section */}
            <div className="col-span-2 mt-4">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                Associated Contacts
              </h4>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Contacts
              </label>
              <div className="border border-gray-300 rounded-lg p-4 max-h-60 overflow-y-auto">
                {allContacts.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No contacts available. Create contacts first from the
                    Contacts menu.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {allContacts.map((contact) => (
                      <label
                        key={contact.id}
                        className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(contact.id)}
                          onChange={() => handleContactToggle(contact.id)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {contact.first_name} {contact.last_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {contact.primary_email}
                          </div>
                        </div>
                        <span
                          className={`text-xs px-2 py-1 rounded-full ${
                            contact.status === "Active"
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {contact.status}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Selected: {selectedContacts.length} contact(s)
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 p-6 border-t">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {title.includes("Create") ? "Create Vendor" : "Update Vendor"}
          </button>
        </div>
      </div>
    </div>
  );
};

const VendorManagement = () => {
  const { showToast } = useToast();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [allContacts, setAllContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [formData, setFormData] = useState({
    company_name: "",
    parent_company: "",
    status: "Qualified Lead",
    company_website: "",
    ownership_type: "",
    company_overview: "",
    company_description: "",
    industry: "",
    currency: "USD",
    standard_perm_fee_percentage: "",
    account_manager: "",
    main_phone: "",
    address: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "USA",
    billing_contact: "",
    billing_frequency: "Monthly",
    billing_phone: "",
    billing_email: "",
    billing_address: "",
  });

  useEffect(() => {
    fetchCompanies();
    fetchAllContacts();
  }, [statusFilter]);

  const fetchAllContacts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contacts`);
      if (response.ok) {
        const data = await response.json();
        setAllContacts(data.contacts || []);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/companies`;
      if (statusFilter) {
        url += `?status=${encodeURIComponent(statusFilter)}`;
      }
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      } else {
        showToast("Failed to fetch vendors", "error");
      }
    } catch (error) {
      console.error("Error fetching companies:", error);
      showToast("Error loading vendors", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompany = async () => {
    // Validate mandatory fields
    if (
      !formData.company_name ||
      !formData.status ||
      !formData.account_manager ||
      !formData.main_phone ||
      !formData.address ||
      !formData.billing_contact ||
      !formData.billing_phone
    ) {
      showToast("Please fill all mandatory fields", "warning");
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/companies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          created_by: "current_user@juno.com", // TODO: Replace with actual user
        }),
      });

      if (response.ok) {
        const result = await response.json();
        showToast("Vendor created successfully", "success");
        setShowCreateModal(false);
        resetForm();
        fetchCompanies();
      } else {
        const error = await response.json();
        showToast(error.detail || "Failed to create vendor", "error");
      }
    } catch (error) {
      console.error("Error creating company:", error);
      showToast("Error creating vendor", "error");
    }
  };

  const handleUpdateCompany = async () => {
    if (!selectedCompany) return;

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/companies/${selectedCompany.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        showToast("Vendor updated successfully", "success");
        setShowEditModal(false);
        setSelectedCompany(null);
        resetForm();
        fetchCompanies();
      } else {
        const error = await response.json();
        showToast(error.detail || "Failed to update vendor", "error");
      }
    } catch (error) {
      console.error("Error updating company:", error);
      showToast("Error updating vendor", "error");
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm("Are you sure you want to delete this vendor?")) {
      return;
    }

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/companies/${companyId}`,
        {
          method: "DELETE",
        }
      );

      if (response.ok) {
        showToast("Vendor deleted successfully", "success");
        fetchCompanies();
      } else {
        showToast("Failed to delete vendor", "error");
      }
    } catch (error) {
      console.error("Error deleting company:", error);
      showToast("Error deleting vendor", "error");
    }
  };

  const openEditModal = (company) => {
    setSelectedCompany(company);
    setFormData({
      company_name: company.company_name || "",
      parent_company: company.parent_company || "",
      status: company.status || "Qualified Lead",
      company_website: company.company_website || "",
      ownership_type: company.ownership_type || "",
      company_overview: company.company_overview || "",
      company_description: company.company_description || "",
      industry: company.industry || "",
      currency: company.currency || "USD",
      standard_perm_fee_percentage: company.standard_perm_fee_percentage || "",
      account_manager: company.account_manager || "",
      main_phone: company.main_phone || "",
      address: company.address || "",
      address_line1: company.address_line1 || "",
      address_line2: company.address_line2 || "",
      city: company.city || "",
      state: company.state || "",
      zip_code: company.zip_code || "",
      country: company.country || "USA",
      billing_contact: company.billing_contact || "",
      billing_frequency: company.billing_frequency || "Monthly",
      billing_phone: company.billing_phone || "",
      billing_email: company.billing_email || "",
      billing_address: company.billing_address || "",
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      company_name: "",
      parent_company: "",
      status: "Qualified Lead",
      company_website: "",
      ownership_type: "",
      company_overview: "",
      company_description: "",
      industry: "",
      currency: "USD",
      standard_perm_fee_percentage: "",
      account_manager: "",
      main_phone: "",
      address: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      zip_code: "",
      country: "USA",
      billing_contact: "",
      billing_frequency: "Monthly",
      billing_phone: "",
      billing_email: "",
      billing_address: "",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      Unqualified: "bg-gray-100 text-gray-800",
      "Qualified Lead": "bg-blue-100 text-blue-800",
      Proposal: "bg-yellow-100 text-yellow-800",
      Negotiation: "bg-orange-100 text-orange-800",
      "Active Account": "bg-green-100 text-green-800",
      "Passive Account": "bg-purple-100 text-purple-800",
      DNC: "bg-red-100 text-red-800",
      Archive: "bg-gray-100 text-gray-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const filteredCompanies = companies.filter(
    (company) =>
      company.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.industry?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.account_manager?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vendors</h1>
          <p className="text-gray-600 mt-1">Manage your vendor database</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          <span>Add Vendor</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-end space-x-2">
            <span className="text-sm text-gray-600">
              {filteredCompanies.length} vendors
            </span>
          </div>
        </div>
      </div>

      {/* Companies Table */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading vendors...</p>
        </div>
      ) : filteredCompanies.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No vendors found
          </h3>
          <p className="text-gray-600">
            Get started by adding your first vendor
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vendor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Industry
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account Manager
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCompanies.map((company) => (
                <tr key={company.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {company.company_name}
                        </div>
                        {company.parent_company && (
                          <div className="text-sm text-gray-500">
                            Parent: {company.parent_company}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                        company.status
                      )}`}
                    >
                      {company.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {company.industry || "-"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center text-sm text-gray-900">
                      <User className="w-4 h-4 mr-2 text-gray-400" />
                      {company.account_manager}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                        {company.main_phone}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => openEditModal(company)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Edit"
                      >
                        <Edit className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteCompany(company.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Vendor Modal */}
      <VendorModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          resetForm();
        }}
        onSave={handleCreateCompany}
        title="Create New Vendor"
        formData={formData}
        setFormData={setFormData}
        allContacts={allContacts}
        selectedContacts={selectedContacts}
        setSelectedContacts={setSelectedContacts}
      />

      {/* Edit Vendor Modal */}
      <VendorModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedCompany(null);
          resetForm();
        }}
        onSave={handleUpdateCompany}
        title="Edit Vendor"
        formData={formData}
        setFormData={setFormData}
        allContacts={allContacts}
        selectedContacts={selectedContacts}
        setSelectedContacts={setSelectedContacts}
      />
    </div>
  );
};

export default VendorManagement;
